import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';

import {
  buildCanvasDoc,
  replaceColor,
  serializeEditableDocument,
  tokenChipMarkup,
} from './emailTemplateEditing';

/** Imperative surface used by the page toolbar (insert token, recolour). */
export interface EmailDesignCanvasHandle {
  insertToken: (name: string) => void;
  applyAccentColor: (fromHex: string, toHex: string) => void;
}

interface EmailDesignCanvasProps {
  /** Body source HTML (with `{{tokens}}`). Captured once on mount — reseed via React `key`. */
  sourceHtml: string;
  /** Rendered width of the email (px number for mobile, '100%' for desktop). */
  width: number | string;
  /** Emits the cleaned source HTML (tokens restored) after each edit. */
  onChange: (html: string) => void;
}

const DEBOUNCE_MS = 300;

/**
 * The visual ("Diseño") editing surface: the rendered email inside an isolated,
 * same-origin iframe whose body is `contenteditable`. Tokens are shown as
 * protected chips so text editing can never split a `{{token}}`. The component is
 * intentionally *uncontrolled* — it seeds from `sourceHtml` once on mount and
 * emits changes upward; the parent forces a reseed by changing the React `key`.
 * This avoids the keystroke-by-keystroke reload that a controlled `srcDoc` would
 * cause. Inline text + accent recolouring live here; structural edits belong in
 * the Código tab (a deliberate, communicated split).
 *
 * Security: `sandbox="allow-same-origin"` lets the parent read/write the iframe
 * DOM but withholds `allow-scripts`, so any `<script>` an admin pastes never runs.
 */
export const EmailDesignCanvas = forwardRef<EmailDesignCanvasHandle, EmailDesignCanvasProps>(
  function EmailDesignCanvas({ sourceHtml, width, onChange }, ref) {
    const iframeRef = useRef<HTMLIFrameElement>(null);
    const onChangeRef = useRef(onChange);
    onChangeRef.current = onChange;

    const timerRef = useRef<number | null>(null);
    const pendingRef = useRef(false);
    const savedRangeRef = useRef<Range | null>(null);

    // Capture the seed exactly once per mount; the parent reseeds via `key`.
    const [initialDoc] = useState(() => buildCanvasDoc(sourceHtml));

    const currentDoc = (): Document | null => iframeRef.current?.contentDocument ?? null;

    const emitNow = (): void => {
      const doc = currentDoc();
      if (doc === null) {
        return;
      }
      pendingRef.current = false;
      onChangeRef.current(serializeEditableDocument(doc));
    };

    const scheduleEmit = (): void => {
      pendingRef.current = true;
      if (timerRef.current !== null) {
        window.clearTimeout(timerRef.current);
      }
      timerRef.current = window.setTimeout(emitNow, DEBOUNCE_MS);
    };

    const handleLoad = (): void => {
      const doc = currentDoc();
      if (doc === null) {
        return;
      }
      const win = doc.defaultView;
      doc.addEventListener('input', scheduleEmit);
      doc.addEventListener('selectionchange', () => {
        const sel = win?.getSelection();
        if (sel !== null && sel !== undefined && sel.rangeCount > 0) {
          savedRangeRef.current = sel.getRangeAt(0).cloneRange();
        }
      });
    };

    useImperativeHandle(
      ref,
      () => ({
        insertToken: (name: string): void => {
          const doc = currentDoc();
          const win = doc?.defaultView ?? null;
          if (doc === null || win === null) {
            return;
          }
          win.focus();
          const range = savedRangeRef.current;
          if (range !== null) {
            const sel = win.getSelection();
            sel?.removeAllRanges();
            sel?.addRange(range);
          }
          // execCommand is deprecated but remains the pragmatic, broadly-supported
          // way to splice HTML at a contenteditable caret; the parent drives it on
          // the same-origin child document.
          const inserted = doc.execCommand('insertHTML', false, `${tokenChipMarkup(name)}&#8203;`);
          if (!inserted) {
            doc.body?.insertAdjacentHTML('beforeend', tokenChipMarkup(name));
          }
          emitNow();
        },
        applyAccentColor: (fromHex: string, toHex: string): void => {
          const doc = currentDoc();
          const iframe = iframeRef.current;
          if (doc === null || iframe === null) {
            return;
          }
          const recoloured = replaceColor(serializeEditableDocument(doc), fromHex, toHex);
          iframe.srcdoc = buildCanvasDoc(recoloured);
          onChangeRef.current(recoloured);
        },
      }),
      // eslint-disable-next-line react-hooks/exhaustive-deps -- handle only reads refs/stable module fns
      [],
    );

    // Flush a pending edit when the canvas unmounts (tab switch / type change)
    // so no in-flight keystroke is lost before the debounce fires. The iframe DOM
    // is still reachable during effect cleanup (React removes it afterwards).
    useEffect(
      () => () => {
        if (timerRef.current !== null) {
          window.clearTimeout(timerRef.current);
        }
        if (pendingRef.current) {
          emitNow();
        }
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps -- run only on unmount
      [],
    );

    return (
      <div className="rounded-lg border border-slate-200 bg-slate-100 p-3">
        <div className="mx-auto transition-all" style={{ width, maxWidth: 600 }}>
          <iframe
            ref={iframeRef}
            title="Editor visual del correo"
            sandbox="allow-same-origin"
            srcDoc={initialDoc}
            onLoad={handleLoad}
            className="h-[600px] w-full rounded-md border border-slate-200 bg-white"
            data-testid="design-canvas"
          />
        </div>
        <p className="mt-2 text-center text-xs text-slate-400">
          Haz clic en cualquier texto para editarlo. Los tokens aparecen como etiquetas y no se pueden romper.
        </p>
      </div>
    );
  },
);
